import { isHexColor } from '@dl/shared';
import {
  colorPicker as lluiColorPicker,
  type ColorPickerMsg as LluiColorPickerMsg,
  type ColorPickerState as LluiColorPickerState,
} from '@llui/components/color-picker';
import {
  button,
  constant,
  div,
  input as inputEl,
  span,
  type ElProps,
  type Mountable,
  type Reactive,
  type Send,
  type Signal,
} from '@llui/dom';

/**
 * `<ColorPicker>` — the DungeonLogs composition over LLui's standard color
 * picker. The host owns the value and commits through `onChange`; LLui owns
 * swatch selection state, semantics, and connected part attributes.
 *
 * Three coordinated affordances, all committing the same `#rrggbb`(`aa`)
 * value:
 *
 *   1. A curated **swatch row** — LLui's standard pressed swatches (the
 *      §10.2.1 campaign-accent palette by default). Picking one commits
 *      its hex immediately.
 *   2. An optional native `<input type="color">` for arbitrary picking
 *      (drag/eyedropper) — the "custom" affordance.
 *   3. An optional free **hex text field** — the manual-entry path AND
 *      the **non-colour a11y cue** (the structural invariant: colour is
 *      never the only signal; the active swatch ALSO carries a ✓ glyph
 *      and `aria-checked`, and the hex string mirrors the value).
 *
 * a11y: the swatches are real buttons with LLui-managed `aria-pressed`; the
 * active swatch shows a check glyph and each has a text `aria-label`, so the
 * choice reads without colour.
 * Every control is keyboard-reachable (native button/input focus order)
 * with a visible `:focus-visible` ring (CSS). All copy flows through the
 * host-supplied `messages` — no hardcoded English.
 *
 * Construction (host owns state):
 *
 *   colorPicker({
 *     value: currentHex,                 // Reactive<string>
 *     swatches: CAMPAIGN_ACCENT_SWATCHES.map((s) => ({ value: s.hex, label: t(...) })),
 *     onChange: (hex) => commit(hex),
 *     messages: { group: t('color.group'), ... },
 *   })
 *
 * CSS lives in `./color-picker.css` (exported via package.json).
 */

/** One curated colour choice in the swatch row. */
export interface ColorSwatch {
  /** The committed value — a `#rrggbb` / `#rrggbbaa` sRGB hex string. */
  value: string;
  /** Accessible, localised name (e.g. "Teal"). Doubles as the non-colour identifier. */
  label: string;
}

/** All localisable copy for the control. Host-supplied (no hardcoded English). */
export interface ColorPickerMessages {
  /** Label for the whole control group. */
  group: string;
  /** Label for the curated-swatch `radiogroup`. */
  swatches: string;
  /** Accessible label for the native `<input type="color">`. */
  custom: string;
  /** Accessible label for the hex text field. */
  hex: string;
  /** Placeholder for the hex text field. */
  hexPlaceholder: string;
}

/** Sensible English fallbacks; real surfaces pass localised `messages`. */
export const DEFAULT_COLOR_PICKER_MESSAGES: ColorPickerMessages = {
  group: 'Colour',
  swatches: 'Preset colours',
  custom: 'Custom colour',
  hex: 'Hex value',
  hexPlaceholder: '#rrggbb',
};

export interface ColorPickerOptions {
  /** Controlled value — static or reactive. May be empty/invalid (renders no active swatch). */
  value: Reactive<string>;
  /** Commit a chosen/typed hex. The atom does not validate beyond what the swatch/native need. */
  onChange: (hex: string) => void;
  /** The curated swatch row. Pass `[]` to hide it (then `showHex`/`showNative` carry the control). */
  swatches: readonly ColorSwatch[];
  /** Show the native `<input type="color">` arbitrary picker. Default `true`. */
  showNative?: boolean;
  /** Show the free hex text field (the non-colour a11y cue). Default `true`. */
  showHex?: boolean;
  /** Localised copy; merged over {@link DEFAULT_COLOR_PICKER_MESSAGES}. */
  messages?: Partial<ColorPickerMessages>;
  /** Stable id base — the native swatch gets `${id}`, the hex field `${id}-hex`. */
  id?: string;
  /** `data-testid` on the group container. */
  testid?: string;
  /** `data-testid` on the hex field (consumers assert the value through it). */
  hexTestid?: string;
  /** Extra class(es) appended after the base class. */
  className?: string;
  /** Focus passthrough (e.g. fact-row focus tracking). Fires for any sub-control. */
  onFocus?: () => void;
  /** Blur passthrough. */
  onBlur?: () => void;
  /** Arbitrary extra attrs merged onto the hex field (e.g. `aria-describedby`). */
  hexAttrs?: ElProps;
}

/** A `Reactive<T>` is a signal when it carries `.map`; a bare value is not. */
function isSignal<T>(r: Reactive<T>): r is Signal<T> {
  return r != null && typeof (r as { map?: unknown }).map === 'function';
}

/** Map a `Reactive<T>` through `fn`, preserving reactivity. */
function mapReactive<T, U>(r: Reactive<T>, fn: (v: T) => U): Reactive<U> {
  return isSignal(r) ? r.map(fn) : fn(r);
}

/**
 * The native `<input type="color">` needs a 6-digit `#rrggbb`. The stored
 * value may carry an alpha byte (which the native widget can't show) or be
 * empty/invalid — fall back to black so the swatch always has a value.
 */
function nativeHex(raw: string): string {
  return isHexColor(raw) ? raw.slice(0, 7) : '#000000';
}

function pickerState(raw: string): LluiColorPickerState {
  const parsed = lluiColorPicker.parseColor(raw);
  return parsed
    ? lluiColorPicker.init({ hsl: parsed.hsl, alpha: parsed.alpha ?? 1 })
    : lluiColorPicker.init();
}

/** A curated swatch using LLui's complete connected part bag. */
function swatchButton(
  swatch: ColorSwatch,
  parts: ReturnType<typeof lluiColorPicker.connect>,
  opts: ColorPickerOptions,
): Mountable {
  return button(
    {
      ...parts.swatch(swatch.value),
      class: 'dl-color-picker-swatch',
      'aria-label': swatch.label,
      'data-testid': `color-swatch-${swatch.value}`,
      ...(opts.onFocus ? { onFocus: () => opts.onFocus!() } : {}),
      ...(opts.onBlur ? { onBlur: () => opts.onBlur!() } : {}),
    },
    [
      // Product invariant: selection must have a non-colour cue.
      span({ class: 'dl-color-picker-check', 'aria-hidden': 'true' }, ['✓']),
    ],
  );
}

export function colorPicker(opts: ColorPickerOptions): Mountable {
  const msg = { ...DEFAULT_COLOR_PICKER_MESSAGES, ...(opts.messages ?? {}) };
  const showNative = opts.showNative ?? true;
  const showHex = opts.showHex ?? true;
  const klass = `dl-color-picker${opts.className ? ` ${opts.className}` : ''}`;
  const state = isSignal(opts.value)
    ? opts.value.map((raw) => pickerState(raw))
    : constant(pickerState(opts.value));
  const send: Send<LluiColorPickerMsg> = (message) => {
    // Only swatches use the machine's mutation path. Manual/native fields keep
    // their commit-on-change contract so fact/config edits do not append an op
    // for every partially typed hex character.
    if (message.type === 'setColor') opts.onChange(message.color);
  };
  const parts = lluiColorPicker.connect(state, send, {
    hexLabel: msg.hex,
    swatchGroupLabel: msg.swatches,
  });

  const children: Mountable[] = [];

  if (opts.swatches.length > 0) {
    children.push(
      div(
        {
          ...parts.swatchGroup,
          class: 'dl-color-picker-swatches',
          'aria-label': msg.swatches,
        },
        opts.swatches.map((swatch) => swatchButton(swatch, parts, opts)),
      ),
    );
  }

  const custom: Mountable[] = [];
  if (showNative) {
    custom.push(
      inputEl({
        type: 'color',
        class: 'dl-color-picker-native',
        value: mapReactive(opts.value, nativeHex),
        'aria-label': msg.custom,
        ...(opts.id !== undefined ? { id: opts.id } : {}),
        ...(opts.onFocus ? { onFocus: () => opts.onFocus!() } : {}),
        ...(opts.onBlur ? { onBlur: () => opts.onBlur!() } : {}),
        onInput: (event: Event) => opts.onChange((event.target as HTMLInputElement).value),
      }),
    );
  }
  if (showHex) {
    const { onInput: _onInput, value: _value, ...hexParts } = parts.hexInput;
    custom.push(
      inputEl({
        ...hexParts,
        type: 'text',
        class: 'dl-color-picker-hex',
        value: opts.value,
        placeholder: msg.hexPlaceholder,
        'aria-label': msg.hex,
        ...(opts.hexAttrs ?? {}),
        ...(opts.id !== undefined ? { id: `${opts.id}-hex` } : {}),
        ...(opts.hexTestid !== undefined ? { 'data-testid': opts.hexTestid } : {}),
        ...(opts.onFocus ? { onFocus: () => opts.onFocus!() } : {}),
        ...(opts.onBlur ? { onBlur: () => opts.onBlur!() } : {}),
        onChange: (event: Event) => {
          opts.onChange((event.target as HTMLInputElement).value);
        },
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'Enter') opts.onChange((event.target as HTMLInputElement).value);
        },
      }),
    );
  }
  if (custom.length > 0) {
    children.push(div({ class: 'dl-color-picker-custom' }, custom));
  }

  return div(
    {
      ...parts.root,
      class: klass,
      role: 'group',
      'aria-label': msg.group,
      ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
    },
    children,
  );
}
